/**
 * AdminProjectStages — SLA/Label/Beschreibung pro Stage editieren + Energieberater pflegen.
 * (Iter88zt) User-Anforderung Novatus 15.08.2026.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Plus, Check, X } from "lucide-react";
import api from "@/lib/api";

export default function AdminProjectStages() {
  const [stages, setStages] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [newAdvisor, setNewAdvisor] = useState({ name: "", email: "", phone: "", is_default: false });

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/admin/project-stages"),
        api.get("/admin/energy-advisors"),
      ]);
      setStages((s.data.stages || []).map(x => ({ ...x, _dirty: false })));
      setAdvisors(a.data.advisors || []);
    } catch { toast.error("Laden fehlgeschlagen"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateField = (key, field, value) => {
    setStages(stages.map(s => s.key === key ? { ...s, [field]: value, _dirty: true } : s));
  };

  const saveStage = async (stage) => {
    setSaving(`stage-${stage.key}`);
    try {
      await api.patch(`/admin/project-stages/${stage.key}`, {
        sla_days: Number(stage.sla_days) || 0,
        label: stage.label,
        description: stage.description,
        customer_label: stage.customer_label,
      });
      toast.success(`"${stage.label}" gespeichert`);
      setStages(stages.map(s => s.key === stage.key ? { ...s, _dirty: false } : s));
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(null); }
  };

  const addAdvisor = async () => {
    if (newAdvisor.name.length < 2 || !newAdvisor.email.includes("@")) {
      toast.error("Name & gültige E-Mail nötig"); return;
    }
    setSaving("advisor-new");
    try {
      await api.post("/admin/energy-advisors", newAdvisor);
      toast.success("Energieberater hinzugefügt");
      setNewAdvisor({ name: "", email: "", phone: "", is_default: false });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
    finally { setSaving(null); }
  };

  const toggleDefault = async (advisor) => {
    setSaving(`advisor-${advisor.id}`);
    try {
      await api.patch(`/admin/energy-advisors/${advisor.id}`, { is_default: !advisor.is_default });
      await load();
    } catch { toast.error("Fehlgeschlagen"); }
    finally { setSaving(null); }
  };

  const deleteAdvisor = async (advisor) => {
    if (!window.confirm(`"${advisor.name}" deaktivieren?`)) return;
    try {
      await api.delete(`/admin/energy-advisors/${advisor.id}`);
      toast.success("Deaktiviert"); await load();
    } catch { toast.error("Fehlgeschlagen"); }
  };

  if (loading) return <div className="p-6 text-zinc-500"><Loader2 className="animate-spin inline mr-2" size={14} /> Lade …</div>;

  const bzaStages = stages.filter(s => ["bza_requested","bza_received","bza_sent_to_customer","kfw_confirmation_received","first_az_sent","project_planning_done"].includes(s.key));

  return (
    <div className="p-4 sm:p-6 max-w-4xl" data-testid="admin-project-stages-page">
      <h1 className="text-2xl font-black text-zinc-100 mb-1">Prozess-Konfiguration</h1>
      <p className="text-[12.5px] text-zinc-500 mb-6">SLA-Fristen und Beschreibungen pro Post-Signatur-Stage. Änderungen greifen sofort im Kanban.</p>

      <section className="space-y-3 mb-8">
        <h2 className="text-lg font-bold text-zinc-100">BzA-Prozesskette (6 Stages)</h2>
        {bzaStages.map(stage => (
          <div key={stage.key} className="rounded-lg border border-zinc-800 bg-zinc-900/70 p-4" data-testid={`stage-row-${stage.key}`}>
            <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_2fr_auto] gap-2 items-center">
              <input
                value={stage.label} onChange={(e) => updateField(stage.key, "label", e.target.value)}
                className="h-10 rounded bg-zinc-800 border border-zinc-700 px-2 text-[13px] font-bold text-zinc-100"
                data-testid={`stage-label-${stage.key}`}
              />
              <div className="flex items-center gap-1">
                <input
                  type="number" min="0" max="365"
                  value={stage.sla_days || 0}
                  onChange={(e) => updateField(stage.key, "sla_days", e.target.value)}
                  className="h-10 w-20 rounded bg-zinc-800 border border-zinc-700 px-2 text-[13px] text-zinc-100 text-center"
                  data-testid={`stage-sla-${stage.key}`}
                />
                <span className="text-[11px] text-zinc-500">Tage SLA</span>
              </div>
              <input
                value={stage.customer_label || ""} onChange={(e) => updateField(stage.key, "customer_label", e.target.value)}
                placeholder="Kunden-Label"
                className="h-10 rounded bg-zinc-800 border border-zinc-700 px-2 text-[12.5px] text-zinc-300"
              />
              <button
                onClick={() => saveStage(stage)}
                disabled={!stage._dirty || saving === `stage-${stage.key}`}
                className="h-10 px-3 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-900 text-[12px] font-bold inline-flex items-center gap-1 disabled:opacity-40"
                data-testid={`stage-save-${stage.key}`}
              >
                {saving === `stage-${stage.key}` ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />}
                Speichern
              </button>
            </div>
            <textarea
              value={stage.description || ""} onChange={(e) => updateField(stage.key, "description", e.target.value)}
              rows={2}
              placeholder="Beschreibung (intern)"
              className="mt-2 w-full rounded bg-zinc-800 border border-zinc-700 px-2 py-1.5 text-[12px] text-zinc-300"
            />
          </div>
        ))}
      </section>

      <section>
        <h2 className="text-lg font-bold text-zinc-100 mb-2">Energieberater</h2>
        <div className="space-y-2 mb-4">
          {advisors.map(a => (
            <div key={a.id} className="rounded-lg border border-zinc-800 bg-zinc-900/70 p-3 flex items-center gap-3" data-testid={`advisor-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[13px] text-zinc-100 truncate">
                  {a.name}
                  {a.is_default && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/25 text-emerald-300">DEFAULT</span>}
                </div>
                <div className="text-[11.5px] text-zinc-500 truncate">{a.email} · {a.phone || "—"}</div>
              </div>
              <button
                onClick={() => toggleDefault(a)}
                disabled={saving === `advisor-${a.id}`}
                className={`h-9 px-3 rounded text-[11.5px] font-bold ${a.is_default ? "bg-zinc-700 text-zinc-400" : "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"}`}
                data-testid={`advisor-toggle-default-${a.id}`}
              >
                {a.is_default ? "Ist Default" : "Als Default"}
              </button>
              <button onClick={() => deleteAdvisor(a)} className="h-9 w-9 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 inline-flex items-center justify-center" data-testid={`advisor-delete-${a.id}`}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-dashed border-zinc-700 bg-zinc-900/40 p-3">
          <div className="text-[11px] text-zinc-400 mb-2 font-bold">Neuer Energieberater</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input placeholder="Name" value={newAdvisor.name} onChange={(e) => setNewAdvisor({ ...newAdvisor, name: e.target.value })}
              className="h-10 rounded bg-zinc-800 border border-zinc-700 px-2 text-[13px] text-zinc-100" data-testid="new-advisor-name" />
            <input placeholder="E-Mail" value={newAdvisor.email} onChange={(e) => setNewAdvisor({ ...newAdvisor, email: e.target.value })}
              className="h-10 rounded bg-zinc-800 border border-zinc-700 px-2 text-[13px] text-zinc-100" data-testid="new-advisor-email" />
            <input placeholder="Telefon (optional)" value={newAdvisor.phone} onChange={(e) => setNewAdvisor({ ...newAdvisor, phone: e.target.value })}
              className="h-10 rounded bg-zinc-800 border border-zinc-700 px-2 text-[13px] text-zinc-100" data-testid="new-advisor-phone" />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <label className="text-[11.5px] text-zinc-400 inline-flex items-center gap-1.5">
              <input type="checkbox" checked={newAdvisor.is_default} onChange={(e) => setNewAdvisor({ ...newAdvisor, is_default: e.target.checked })} data-testid="new-advisor-default" />
              Als Default festlegen
            </label>
            <button onClick={addAdvisor} disabled={saving === "advisor-new"}
              className="h-9 px-3 rounded bg-blue-500 hover:bg-blue-400 text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="new-advisor-save">
              {saving === "advisor-new" ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />}
              Hinzufügen
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
